import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../api/client';
import { useAuth } from '../auth';
import { messageFromError } from '../components/ApiError';
import type { ApiResponse, Customer } from '../types';

type Currency = 'USD' | 'AUD' | 'GBP' | 'EUR' | 'CAD';
type CustomerForm = {
  name: string;
  companyName?: string;
  email: string;
  phone?: string;
  country?: string;
  currency: Currency;
  notes?: string;
};

const emptyForm: CustomerForm = { name: '', companyName: '', email: '', phone: '', country: '', currency: 'USD', notes: '' };
const baseColumns: GridColDef<Customer>[] = [
  { field: 'name', headerName: 'Customer', flex: 1, minWidth: 160 },
  { field: 'companyName', headerName: 'Company', flex: 1, minWidth: 160 },
  { field: 'email', headerName: 'Email', flex: 1.3, minWidth: 220 },
  { field: 'country', headerName: 'Country', width: 100 },
  { field: 'currency', headerName: 'Currency', width: 100 },
];

function customerForm(customer: Customer): CustomerForm {
  return {
    name: customer.name,
    companyName: customer.companyName ?? '',
    email: customer.email,
    phone: customer.phone ?? '',
    country: customer.country ?? '',
    currency: customer.currency as Currency,
    notes: customer.notes ?? '',
  };
}

export function CustomersPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'ADMIN' || user?.permissions.some((permission) => permission.module === 'CUSTOMERS' && permission.canWrite) === true;
  const canDelete = user?.role === 'ADMIN';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState<Customer | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<CustomerForm>({ defaultValues: emptyForm });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: async () => (await api.get<ApiResponse<Customer[]>>('/customers', { params: { pageSize: 100 } })).data,
  });
  const save = useMutation({
    mutationFn: async ({ customerId, values }: { customerId?: string; values: CustomerForm }) => {
      const payload = { ...values, country: values.country?.toUpperCase() || undefined };
      return customerId ? api.patch(`/customers/${customerId}`, payload) : api.post('/customers', payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      setOpen(false);
      setEditing(null);
      reset(emptyForm);
    },
  });
  const removeCustomer = useMutation({
    mutationFn: (customerId: string) => api.delete(`/customers/${customerId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleting(null);
      setDeleteError(null);
    },
  });

  const startCreate = () => {
    setEditing(null);
    clearErrors();
    reset(emptyForm);
    setOpen(true);
  };
  const startEdit = (customer: Customer) => {
    setEditing(customer);
    clearErrors();
    reset(customerForm(customer));
    setOpen(true);
  };
  const closeForm = () => {
    setOpen(false);
    setEditing(null);
    clearErrors();
    reset(emptyForm);
  };
  const columns = useMemo<GridColDef<Customer>[]>(() => {
    if (!canEdit) return baseColumns;
    return [
      ...baseColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        width: canDelete ? 112 : 64,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit customer">
              <IconButton aria-label={`Edit ${row.name}`} size="small" onClick={(event) => { event.stopPropagation(); startEdit(row); }}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title="Delete customer">
                <IconButton aria-label={`Delete ${row.name}`} size="small" color="error" onClick={(event) => { event.stopPropagation(); setDeleteError(null); setDeleting(row); }}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ];
  }, [canDelete, canEdit]);
  const rows = Array.isArray(customers.data?.data) ? customers.data.data : [];

  return (
    <Stack spacing={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4">Customers</Typography>
          <Typography color="text.secondary">Keep contact and billing records ready for invoicing.</Typography>
        </Box>
        {canEdit && <Button startIcon={<AddRoundedIcon />} variant="contained" onClick={startCreate}>New customer</Button>}
      </Box>

      {customers.isError && <Alert severity="error">{messageFromError(customers.error)}</Alert>}
      {customers.isLoading ? <LinearProgress /> : (
        <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 2 }}>
          <DataGrid rows={rows} columns={columns} disableRowSelectionOnClick initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50, 100]} />
        </Box>
      )}

      <Dialog open={open} onClose={closeForm} fullWidth maxWidth="sm">
        <Box
          component="form"
          onSubmit={handleSubmit(async (values) => {
            try {
              await save.mutateAsync({ customerId: editing?.id, values });
            } catch (error) {
              setError('root', { message: messageFromError(error) });
            }
          })}
        >
          <DialogTitle>{editing ? 'Edit customer' : 'New customer'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} pt={1}>
              {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
              <TextField label="Customer name" error={Boolean(errors.name)} helperText={errors.name?.message} {...register('name', { required: 'Customer name is required' })} />
              <TextField label="Company name" {...register('companyName')} />
              <TextField label="Email" type="email" error={Boolean(errors.email)} helperText={errors.email?.message} {...register('email', { required: 'Email is required' })} />
              <TextField label="Phone" {...register('phone')} />
              <Stack direction="row" spacing={2}>
                <TextField label="Country code" placeholder="US" inputProps={{ maxLength: 2 }} fullWidth {...register('country')} />
                <TextField select label="Currency" fullWidth SelectProps={{ native: true }} {...register('currency')}>
                  <option value="USD">USD</option>
                  <option value="AUD">AUD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                  <option value="CAD">CAD</option>
                </TextField>
              </Stack>
              <TextField label="Notes" multiline minRows={2} {...register('notes')} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting || save.isPending}>{editing ? 'Save changes' : 'Create customer'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => { if (!removeCustomer.isPending) setDeleting(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Delete customer?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>Delete {deleting?.name}? This hides the customer from future invoice selection.</Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={removeCustomer.isPending}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeCustomer.isPending}
            onClick={async () => {
              if (!deleting) return;
              try {
                await removeCustomer.mutateAsync(deleting.id);
              } catch (error) {
                setDeleteError(messageFromError(error));
              }
            }}
          >
            Delete customer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
